<script setup>
import { ref, computed, markRaw } from 'vue'
import { useStore } from 'vuex'

import TableActionMenu from '@/components/common/TableActionMenu.vue'
import OptionField from '@/components/form/OptionField.vue'
import ColorPickerField from '@/components/form/ColorPickerField.vue'
import CheckBoxField from '@/components/form/CheckBoxField.vue'
import DataTableField from '@/components/form/DataTableField.vue'
import SuTableOptions from '@/components/soil-type/SuTableOptions.vue'


import { actionMenuMixin } from '@/mixins/actionMenuMixin'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'

const { dirtyItems, addDirty, getDirtyClass } = dirtyFieldMixin()
const { selectedItem, x, y, showMenu, show } = actionMenuMixin()

// props
const props = defineProps({
  items: { required: true },
  baseItems: { required: true },
  editable: { type: Boolean, default: true }
})

// local state (was data())
const writeLoading = ref(false)
const headers = ref([
  { title: 'Name', width: '20%' },
  { title: 'Saturated unit weight' },
  { title: 'Unsaturated unit weight' },
  { title: 'Water content' },
  { title: 'Soil behaviour', width: '12%' },
  { title: 'Different soil behavior above phreatic line', width: '8%' },
  { title: 'Is cohesive?' },
  { title: 'Cohesion' },
  { title: 'Cohesion (dry torn zone)' },
  { title: 'Phi characteristic' },
  { title: 'Phi characteristic (dry torn zone)' },
  { title: 'Su-table', width: '12%' },
  { title: 'Su-table (dry torn zone)', width: '12%' },
  { title: 'Dilatation' },
  { title: 'S characteristic' },
  { title: 'M characteristic (SHANSEP)' },
  { title: 'M characteristic (Su-table)' },
  { title: 'M characteristic (Su-table, dry torn zone)' },
  { title: 'Pop characteristic' },
  { title: 'Color', width: '8%' }
])

const parameters = ref([
  { parameter: 'saturated_unit_weight', component: markRaw(DataTableField) },
  { parameter: 'unsaturated_unit_weight', component: markRaw(DataTableField) },
  { parameter: 'water_content', component: markRaw(DataTableField) },
  { parameter: 'soil_behaviour', component: markRaw(OptionField) },
  { parameter: 'different_soil_behaviour', component: markRaw(OptionField) },
  { parameter: 'is_cohesive', component: markRaw(CheckBoxField) },
  { parameter: 'cohesion', component: markRaw(DataTableField) },
  { parameter: 'cohesion_dry_torn', component: markRaw(DataTableField) },
  { parameter: 'phi_characteristic', component: markRaw(DataTableField) },
  { parameter: 'phi_characteristic_dry_torn', component: markRaw(DataTableField) },
  { parameter: 'su_table', component: markRaw(SuTableOptions) },
  { parameter: 'su_table_dry_torn', component: markRaw(SuTableOptions) },
  { parameter: 'dilatation', component: markRaw(DataTableField) },
  { parameter: 's_characteristic', component: markRaw(DataTableField) },
  { parameter: 'm_characteristic_shansep', component: markRaw(DataTableField) },
  { parameter: 'm_characteristic_su_table', component: markRaw(DataTableField) },
  { parameter: 'm_characteristic_dry_torn', component: markRaw(DataTableField) },
  { parameter: 'pop_characteristic', component: markRaw(DataTableField) }
])

// vuex
const store = useStore()

// --- mapped state (project/base/others) ---
const baseLoading = computed(() => store.state.baseSoilType.loading)

const loading = computed(() => store.state.soilType.loading)
const options = computed(() => store.state.soilType.options)

const suTableItems = computed(() => store.state.suTable.items)

// derived options
const suTableOptions = computed(() =>
  (suTableItems.value || [])
    .filter((item) => item.id > 0)
    .map((item) => ({ value: item.id, display_name: item.name }))
)

// was a computed using this.dirtyItems
const isChangedBaseSoilType = computed(() =>
  dirtyItems.value.some(
    (item) => item.id && (item.fields.includes('name') || item.fields.includes('color'))
  )
)

// --- actions (wrapped as arrow functions) ---
const setParameter = (payload) => store.dispatch('soilType/setParameter', payload)
const deleteObj = (id) => store.dispatch('soilType/deleteItem', id)
const resetOverviewItems = () => store.dispatch('soilType/resetOverviewItems')
const emptyDirtyItems = () => store.dispatch('soilType/emptyDirtyItems')
const update = () => store.dispatch('soilType/update')

const setBaseParameter = (payload) => store.dispatch('baseSoilType/setParameter', payload)
const updateBase = () => store.dispatch('baseSoilType/update')
const createBase = () => store.dispatch('baseSoilType/create')
const resetBaseOverviewItems = () => store.dispatch('baseSoilType/resetOverviewItems')
const emptyBaseDirtyItems = () => store.dispatch('baseSoilType/emptyDirtyItems')
const deleteBaseObj = (id) => store.dispatch('baseSoilType/deleteItem', id)

// --- methods ---
const getOptions = (parameter) => {
  if (parameter === 'soil_behaviour' || parameter === 'different_soil_behaviour') {
    return options.value
  } else if (parameter === 'su_table' || parameter === 'su_table_dry_torn') {
    return suTableOptions.value
  }
  return []
}

const getBaseSoilTypeById = (baseSoilTypeId) =>
  props.baseItems.find((item) => item.id === baseSoilTypeId)

const deleteRow = async (soilTypeItem) => {
  await deleteObj(soilTypeItem.id)
  const baseSoilType = getBaseSoilTypeById(soilTypeItem.base_soil_type)
  if (baseSoilType) {
    await deleteBaseObj(baseSoilType.id)
  }
}

const saveChanges = async () => {
  writeLoading.value = true
  try {
    await update()
    await updateBase()
    await createBase()
    emptyDirtyItems()
    resetOverviewItems()
    emptyBaseDirtyItems()
    resetBaseOverviewItems()
    // clear mixin-managed dirtyItems (if mixin exposes setter, otherwise rely on mixin)
    dirtyItems.value = []
  } catch {
    writeLoading.value = false
  }
  writeLoading.value = false
}

defineExpose({
  saveChanges
})
</script>

<template>
  <div class="overflow-x-scroll" style="max-width: 100%;">
    <slot></slot>
    <v-alert v-if="isChangedBaseSoilType" class="mb-2" dense type="warning" variant="outlined">
      If you change the soil type <strong>name</strong> or <strong>color</strong>, the changes will
      be applied to all soil types with the same name in the other revisions as well in this
      project.
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading || baseLoading"
      class="data-table"
      density="compact"
      disable-sort
      item-key="text"
    >
      <template #loader="{ isActive }">
        <v-progress-linear :active="isActive" color="primary" indeterminate />
      </template>
      <template v-slot:item="{ item }">
        <tr class="soil-type-row" @click="selectedItem = item" @contextmenu="show($event, item)">
          <td>
            <DataTableField
              :dirtyClass="getDirtyClass(getBaseSoilTypeById(item.base_soil_type), 'name')"
              :editable="editable"
              :item="getBaseSoilTypeById(item.base_soil_type)"
              parameter="name"
              type="string"
              @set="setBaseParameter"
              @update="addDirty(getBaseSoilTypeById(item.base_soil_type), 'name')"
            />
          </td>
          <td v-for="parameter in parameters" :key="parameter.parameter">
            <component
              :is="parameter.component"
              :dirtyClass="getDirtyClass(item, parameter.parameter)"
              :editable="editable"
              :item="item"
              :options="getOptions(parameter.parameter)"
              :parameter="parameter.parameter"
              @set="setParameter"
              @update="addDirty(item, parameter.parameter)"
            />
          </td>
          <td>
            <ColorPickerField
              v-if="getBaseSoilTypeById(item.base_soil_type)"
              :dirtyClass="getDirtyClass(getBaseSoilTypeById(item.base_soil_type), 'color')"
              :editable="editable"
              :item="getBaseSoilTypeById(item.base_soil_type)"
              parameter="color"
              @set="setBaseParameter"
              @update="addDirty(getBaseSoilTypeById(item.base_soil_type), 'color')"
            />
          </td>
        </tr>
      </template>
    </v-data-table>
    <TableActionMenu
      v-if="editable"
      :addButton="false"
      :selected-item="selectedItem"
      :show-menu="showMenu"
      :x="x"
      :y="y"
      @action="deleteRow(selectedItem)"
      @update:show-menu="showMenu = $event"
    />
  </div>
</template>

<style scoped>
:deep(.v-field__field) {
  font-size: 14px;
}

.data-table {
  min-width: 2300px;
  width: 100%;
}

.data-table:deep(th) {
  text-align: left;
  font-size: 0.75rem;
  font-family: Roboto, sans-serif;
  font-weight: bold !important;
  color: rgba(0, 0, 0, 0.6);
}

.data-table:deep(td) {
  padding: 0 !important;
}

.data-table:deep(th) {
  padding: 0 8px !important;
}
</style>
